import { Link, createFileRoute } from "@tanstack/react-router";
import {
	ArrowLeft,
	BarChart3,
	Calendar,
	FileText,
	Pencil,
	Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import {
	type OutreachHubFilters,
	parseHubFilters,
} from "@/components/outreach/hub-filters";
import { useSession } from "@/hooks/use-session";
import {
	useEmailCampaign,
	useScheduleCampaign,
} from "@/hooks/use-email-campaigns";
import { Button } from "@/components/ui/button";
import { ReadWriteOnly } from "@/components/common/read-write-only";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmailMetricsCard } from "@/components/email/EmailMetricsCard";
import { CampaignMetrics } from "@/components/email/CampaignMetrics";
import { RecipientTable } from "@/components/email/RecipientTable";
import { EmailPreview } from "@/components/email/EmailPreview";
import { EmailCreateFlow } from "@/components/email/EmailCreateFlow";
import type { AudienceType } from "@/types/email-campaigns";

export const Route = createFileRoute("/_app/_/contacts/email/$emailId")({
	// `from=outreach` is set when this campaign is opened as a legacy row from
	// the Outreach hub, so it shows a "Back to outreach" control that restores
	// the hub filters. Other entry points (email list, daily briefing,
	// volunteers) omit it and stay as-is.
	validateSearch: (
		search: Record<string, unknown>,
	): { from?: "outreach" } & OutreachHubFilters => ({
		from: search.from === "outreach" ? "outreach" : undefined,
		...parseHubFilters(search),
	}),
	component: EmailDetailPage,
});

function EmailDetailPage() {
	const { emailId } = Route.useParams();
	const { from, ...filters } = Route.useSearch();
	const session = useSession();
	const campaignId = session.campaign?.id;
	const emailCampaignId = Number(emailId);

	const [showPreview, setShowPreview] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [showScheduleDialog, setShowScheduleDialog] = useState(false);
	const [scheduleDateTime, setScheduleDateTime] = useState("");

	const { data: campaign, isLoading } = useEmailCampaign(
		campaignId!,
		emailCampaignId,
	);
	const scheduleMutation = useScheduleCampaign(campaignId!);

	if (!campaignId || isLoading) {
		return (
			<div className="flex min-h-[50vh] items-center justify-center">
				<p className="text-muted-foreground">Loading...</p>
			</div>
		);
	}

	if (!campaign) {
		return (
			<div className="flex min-h-[50vh] items-center justify-center">
				<p className="text-muted-foreground">Campaign not found.</p>
			</div>
		);
	}

	async function handleSchedule() {
		if (!scheduleDateTime) {
			toast.error("Please select a date and time.");
			return;
		}
		try {
			await scheduleMutation.mutateAsync({
				emailCampaignId,
				data: { send_at: new Date(scheduleDateTime).toISOString() },
			});
			toast.success("Email campaign scheduled!");
			setShowScheduleDialog(false);
			setScheduleDateTime("");
		} catch {
			toast.error("Failed to schedule email campaign.");
		}
	}

	const isDraft = campaign.status === "draft";
	const isFailed = campaign.status === "failed";
	const isEditable = isDraft || isFailed;
	const isSent = campaign.status === "sent" || campaign.status === "sending";

	// Edit mode: render inline EmailCreateFlow
	if (editMode && isEditable) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-4xl">
					<div className="mb-6 flex items-center gap-3">
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setEditMode(false)}
						>
							<ArrowLeft className="h-4 w-4" />
						</Button>
						<div>
							<h1 className="text-2xl font-bold">Edit Email</h1>
							<p className="text-sm text-muted-foreground">
								{campaign.subject || "(No subject)"}
							</p>
						</div>
					</div>
					<EmailCreateFlow
						existingCampaignId={emailCampaignId}
						initialData={{
							subject: campaign.subject,
							preheader: campaign.preheader ?? undefined,
							bodyHtml: campaign.body_html,
							fromName: campaign.from_name,
							fromEmail: campaign.from_email,
							disclaimerText: campaign.disclaimer_text ?? undefined,
							templateId: campaign.template_id,
							thingsToUpdate: campaign.things_to_update ?? [],
							audiences: campaign.audiences
								?.filter((a) => a.audience_type !== "exclusion_group")
								.map((a) => ({
									type: a.audience_type as AudienceType,
									id: a.action_group_id ?? undefined,
									contact_id: a.contact_id ?? undefined,
									// Carry the volunteer filter so a saved filtered volunteer
									// audience reopens narrowed, not broadened to all volunteers.
									volunteer_filter: a.volunteer_filter ?? null,
								})) ?? [],
							exclusions: campaign.audiences
								?.filter((a) => a.audience_type === "exclusion_group")
								.map((a) => ({
									type: "exclusion_group" as AudienceType,
									id: a.action_group_id ?? undefined,
								})) ?? [],
						}}
						onCancel={() => setEditMode(false)}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-4xl space-y-6">
				{from === "outreach" && (
					<Link
						to="/outreach"
						search={filters}
						className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
					>
						<ArrowLeft className="h-3.5 w-3.5" />
						Back to outreach
					</Link>
				)}
				{isEditable && (
					<div className="flex justify-end">
						<ReadWriteOnly message="Read-only — you can't edit"><Button
							variant="outline"
							size="sm"
							onClick={() => setEditMode(true)}
							className="gap-1"
						>
							<Pencil className="h-3.5 w-3.5" />
							Edit
						</Button></ReadWriteOnly>
					</div>
				)}

				{/* Tab layout for sent campaigns; flat layout for drafts */}
				{isSent ? (
					<Tabs defaultValue="metrics">
						<TabsList>
							<TabsTrigger value="metrics" className="gap-1.5">
								<BarChart3 className="h-3.5 w-3.5" />
								Metrics
							</TabsTrigger>
							<TabsTrigger value="recipients" className="gap-1.5">
								<Users className="h-3.5 w-3.5" />
								Recipients
							</TabsTrigger>
							<TabsTrigger value="details" className="gap-1.5">
								<FileText className="h-3.5 w-3.5" />
								Details
							</TabsTrigger>
						</TabsList>

						<TabsContent value="metrics" className="mt-4 space-y-4">
							<CampaignMetrics
								campaignId={campaignId}
								emailCampaignId={emailCampaignId}
								sentAt={campaign.sent_at}
							/>
						</TabsContent>

						<TabsContent value="recipients" className="mt-4">
							<RecipientTable
								campaignId={campaignId}
								emailCampaignId={emailCampaignId}
							/>
						</TabsContent>

						<TabsContent value="details" className="mt-4 space-y-4">
							<CampaignDetailsSection campaign={campaign} />
						</TabsContent>
					</Tabs>
				) : (
					<>
						{/* Simple metrics card for non-sent campaigns (shows nothing) */}
						<EmailMetricsCard campaign={campaign} />
						<CampaignDetailsSection campaign={campaign} />
					</>
				)}
			</div>

			{/* Preview modal */}
			<EmailPreview
				open={showPreview}
				onOpenChange={setShowPreview}
				subject={campaign.subject}
				preheader={campaign.preheader}
				html={campaign.body_html}
				campaignId={campaignId}
				emailCampaignId={emailCampaignId}
			/>

			{/* Schedule dialog */}
			<Dialog
				open={showScheduleDialog}
				onOpenChange={setShowScheduleDialog}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Schedule Email</DialogTitle>
						<DialogDescription>
							Choose when to send this email campaign.
						</DialogDescription>
					</DialogHeader>
					<div className="py-4">
						<Label htmlFor="schedule-datetime">Send date & time</Label>
						<Input
							id="schedule-datetime"
							type="datetime-local"
							value={scheduleDateTime}
							onChange={(e) => setScheduleDateTime(e.target.value)}
							min={new Date().toISOString().slice(0, 16)}
							className="mt-2"
						/>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setShowScheduleDialog(false)}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSchedule}
							disabled={
								!scheduleDateTime || scheduleMutation.isPending
							}
							className="gap-1"
						>
							<Calendar className="h-3.5 w-3.5" />
							{scheduleMutation.isPending
								? "Scheduling..."
								: "Schedule"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

/**
 * Campaign details section extracted for reuse in both tab and flat layouts.
 */
function CampaignDetailsSection({
	campaign,
}: { campaign: NonNullable<ReturnType<typeof useEmailCampaign>["data"]> }) {
	return (
		<>
			{/* Details card */}
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-base">Campaign Details</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-3">
						<div className="flex items-start gap-3">
							<span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">
								From
							</span>
							<span className="text-sm">
								{campaign.from_name} &lt;{campaign.from_email}&gt;
							</span>
						</div>
						{campaign.reply_to_email && (
							<div className="flex items-start gap-3">
								<span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">
									Reply-To
								</span>
								<span className="text-sm">
									{campaign.reply_to_email}
								</span>
							</div>
						)}
						<div className="flex items-start gap-3">
							<span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">
								Recipients
							</span>
							<span className="text-sm">
								{campaign.recipient_count?.toLocaleString() ?? 0}
								{campaign.recipient_count_skipped
									? ` (${campaign.recipient_count_skipped} skipped)`
									: ""}
							</span>
						</div>
						{campaign.preheader && (
							<div className="flex items-start gap-3">
								<span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">
									Preheader
								</span>
								<span className="text-sm">
									{campaign.preheader}
								</span>
							</div>
						)}
						{campaign.sent_at && (
							<div className="flex items-start gap-3">
								<span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">
									Sent
								</span>
								<span className="text-sm">
									{formatDetailDateTime(campaign.sent_at)}
								</span>
							</div>
						)}
						{campaign.scheduled_at &&
							campaign.status === "scheduled" && (
								<div className="flex items-start gap-3">
									<span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">
										Scheduled
									</span>
									<span className="text-sm">
										{formatDetailDateTime(
											campaign.scheduled_at,
										)}
									</span>
								</div>
							)}
					</div>
				</CardContent>
			</Card>

			{/* Email body preview inline */}
			{campaign.body_html && (
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-base">
							Email Content
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="rounded-lg border bg-white p-4">
							<div
								className="prose prose-sm max-w-none"
								/* biome-ignore lint: html is from our API */
								dangerouslySetInnerHTML={{
									__html: campaign.body_html,
								}}
							/>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Disclaimer */}
			{campaign.disclaimer_text && (
				<div className="rounded-lg border border-dashed bg-muted/30 px-4 py-3">
					<p className="text-xs text-muted-foreground">
						<span className="font-medium">Disclaimer:</span>{" "}
						{campaign.disclaimer_text}
					</p>
				</div>
			)}
		</>
	);
}

function formatDetailDateTime(dateStr: string | null | undefined): string {
	if (!dateStr) return "-";
	return new Date(dateStr).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}
